import { useRef, useState } from "react";
import { useInView } from "framer-motion";
import { ImageIcon } from "lucide-react";
import { siteData } from "@/lib/aht/pageData";

const BASE = `${process.env.PUBLIC_URL || ""}/images/`;
export const imageUrl = (path) => (path ? `${BASE}${path}` : null);

// [{ src, width }] -> "url 800w, url 1482w"
export const srcSetOf = (sources) =>
  Array.isArray(sources) && sources.length ? sources.map((s) => `${imageUrl(s.src)} ${s.width}w`).join(", ") : undefined;

// Photos that ship as an 800px tile plus the full-size file (max 1600px). Falls back to the full file alone.
export const tileSources = (small, full) => (small ? [{ src: small, width: 800 }, { src: full, width: 1600 }] : undefined);

// Calm placeholder: mist block, captioned with the expected file path.
export function Placeholder({ path, compact = false, showCaption = true }) {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center bg-mist p-4"
      style={{ boxShadow: "inset 0 0 0 1px var(--hairline)" }}
      data-testid="image-placeholder"
    >
      {showCaption && (
        <div className="flex max-w-full flex-col items-center gap-2 text-center text-graphite">
          <ImageIcon aria-hidden="true" size={compact ? 16 : 20} strokeWidth={1.5} />
          {!compact && <span className="t-caption">{siteData.ui.placeholderLabel}</span>}
          <span className="t-caption break-all opacity-90" style={{ fontSize: compact ? 12 : 13 }}>
            images/{path}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Image from /public/images with a locked box (no layout shift).
 * Missing or broken files fall back to a captioned placeholder.
 * Non-priority images are requested once they come within 600px of the viewport. Tiles clipped by a
 * horizontal scroller never intersect, so carousels pass `eager` once the strip itself is near.
 * `sources` ([{ src, width }]) adds a srcset so phones download a smaller file.
 */
export function Img({
  src,
  sources,
  alt,
  className = "",
  rounded = "rounded-tile",
  aspect,
  priority = false,
  eager = false,
  fit = "cover",
  position,
  compact = false,
  sizes,
  testId,
  style,
}) {
  const [state, setState] = useState("loading");
  const ref = useRef(null);
  const near = useInView(ref, { once: true, margin: "600px 0px" });
  const url = imageUrl(src);
  const load = priority || eager || near;
  return (
    <div
      ref={ref}
      className={`relative overflow-hidden ${rounded} ${className}`}
      style={{ aspectRatio: aspect, ...style }}
      data-testid={testId}
      data-image-state={state}
    >
      {url && load && state !== "error" && (
        <img
          src={url}
          srcSet={srcSetOf(sources)}
          sizes={sizes}
          alt={alt}
          loading="eager"
          decoding={priority ? "sync" : "async"}
          fetchPriority={priority ? "high" : "auto"}
          onLoad={(e) => setState(e.currentTarget.naturalWidth > 0 ? "ok" : "error")}
          onError={() => setState("error")}
          style={position ? { objectPosition: position } : undefined}
          className={`absolute inset-0 h-full w-full ${fit === "contain" ? "object-contain" : "object-cover"} ${
            // The priority (hero) image paints at full opacity as soon as it exists; its reveal is
            // choreographed by the hero sequence, and this keeps the browser's LCP mark honest and early.
            priority ? "" : `transition-opacity duration-300 ${state === "ok" ? "opacity-100" : "opacity-0"}`
          }`}
        />
      )}
      {state !== "ok" && <Placeholder path={src} compact={compact} showCaption={state === "error" || !url} />}
    </div>
  );
}
